import React from 'react'
import CloseIcon from '@mui/icons-material/Close'
import {
  ConfigBook, FOTOS_POR_SLIDE, FiltroDoBook, LIMITE_DE_FOTOS, MAXIMO_DA_LEGENDA, PERIODOS, bookVazio, lerValores
} from './disparos'

interface CampoDic { nome: string }

interface Props {
  book: ConfigBook | null | undefined
  /** O HTML usa o book: sem ele configurado, o disparo não salva. */
  usaBook: boolean
  dicionario: { colunas: CampoDic[]; medidas: CampoDic[] }
  periodos: string[]
  aoMudar: (b: ConfigBook | null) => void
}

/**
 * O book de fotos do e-mail. Os parâmetros ficam aqui, no disparo: o portal gera o PPTX sozinho a cada envio, no recorte de cada
 * destinatário, e o HTML só tem o botão ({{book.link}}). Ninguém gera o book à mão.
 */
export default function CardBook({ book, usaBook, dicionario, periodos, aoMudar }: Props) {
  const { colunas } = dicionario

  const explicacao = (
    <p className="ng-explic">
      O portal gera o book (um PPTX com as fotos) <b>sozinho a cada envio</b>, com estes parâmetros e no recorte de cada destinatário. O e-mail leva o link,
      que pede login e vale por 5 dias. No HTML, use <code>{'{{book.link}}'}</code> no botão (veja o guia do e-mail). Quem tem o mesmo recorte no mesmo
      dia compartilha o mesmo arquivo. Na prévia o book não é gerado; no envio e no <b>Testar</b> ele é, e pode levar alguns minutos.
    </p>
  )

  if (!book) {
    return (
      <div className="ng-cartao">
        <div className="ng-cartao-cab">
          <h2>Book de fotos</h2>
          <div className="ng-acoes"><button type="button" className="ng-btn" onClick={() => aoMudar(bookVazio())}>Adicionar book de fotos</button></div>
        </div>
        {explicacao}
        {usaBook && <div className="rh-alerta erro">O HTML usa o book, mas ele ainda não foi configurado. Clique em “Adicionar book de fotos”.</div>}
      </div>
    )
  }

  const mudar = (parcial: Partial<ConfigBook>) => aoMudar({ ...book, ...parcial })
  const opcoes = (atual: string | null | undefined) => (
    <>
      {atual && !colunas.some(c => c.nome === atual) && <option value={atual}>{atual} (fora do dicionário)</option>}
      {colunas.map(c => <option key={c.nome} value={c.nome}>{c.nome}</option>)}
    </>
  )

  const trocarLegenda = (i: number, valor: string) =>
    mudar({ legenda: valor ? book.legenda.map((x, k) => (k === i ? valor : x)) : book.legenda.filter((_, k) => k !== i) })

  const trocarFiltro = (i: number, parcial: Partial<FiltroDoBook>) =>
    mudar({ filtros: book.filtros.map((f, k) => (k === i ? { ...f, ...parcial } : f)) })

  return (
    <div className="ng-cartao">
      <div className="ng-cartao-cab">
        <h2>Book de fotos</h2>
        <div className="ng-acoes"><button type="button" className="ng-btn perigo" onClick={() => aoMudar(null)}>Tirar o book</button></div>
      </div>
      {explicacao}
      {!usaBook && <div className="rh-alerta">O HTML ainda não usa o book: sem <code>{'{{book.link}}'}</code> ele não é gerado no envio.</div>}

      <div className="ng-campos">
        <label className="ng-campo"><span>Coluna do link da foto</span>
          <select value={book.colunaFoto} onChange={e => mudar({ colunaFoto: e.target.value })}>
            <option value="">Escolha a coluna</option>
            {opcoes(book.colunaFoto)}
          </select>
          <small>Só entram fotos do Comptrade (comptrade.com.br).</small>
        </label>
        <label className="ng-campo"><span>Um bloco de slides por…</span>
          <select value={book.agruparPor ?? ''} onChange={e => mudar({ agruparPor: e.target.value || null })}>
            <option value="">Não agrupar (um grupo só)</option>
            {opcoes(book.agruparPor)}
          </select>
          <small>Loja, data, marca… Cada valor ganha seus slides.</small>
        </label>
      </div>

      <div className="ng-campos">
        <label className="ng-campo"><span>Fotos por slide</span>
          <select value={book.fotosPorSlide} onChange={e => mudar({ fotosPorSlide: Number(e.target.value) })}>
            {FOTOS_POR_SLIDE.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label className="ng-campo"><span>Limite de fotos</span>
          <input type="number" min={1} max={LIMITE_DE_FOTOS} value={book.limiteFotos}
                 onChange={e => mudar({ limiteFotos: e.target.value ? Number(e.target.value) : 0 })} />
          <small>Até {LIMITE_DE_FOTOS}. O que passar é cortado e fica anotado no histórico.</small>
        </label>
      </div>

      {book.agruparPor && (
        <label className="ng-campo"><span>Como o grupo aparece no título</span>
          <input maxLength={60} value={book.rotuloDoAgrupamento ?? ''} placeholder="Loja" onChange={e => mudar({ rotuloDoAgrupamento: e.target.value || null })} />
          <small>Em branco, vale o nome da coluna.</small>
        </label>
      )}

      <div className="ng-campo">
        <span>Informações embaixo de cada foto (até {MAXIMO_DA_LEGENDA}; a primeira fica em destaque)</span>
        {book.legenda.map((c, i) => (
          <div key={i} className="dp-linha-book">
            <select value={c} aria-label={`Legenda ${i + 1}`} onChange={e => trocarLegenda(i, e.target.value)}>
              {opcoes(c)}
            </select>
            <button type="button" className="ng-ico perigo" aria-label={`Tirar a legenda ${i + 1}`} onClick={() => trocarLegenda(i, '')}><CloseIcon fontSize="small" /></button>
          </div>
        ))}
        {book.legenda.length < MAXIMO_DA_LEGENDA && (
          <select value="" aria-label="Adicionar informação" onChange={e => e.target.value && mudar({ legenda: [...book.legenda, e.target.value] })}>
            <option value="">Adicionar informação…</option>
            {colunas.filter(c => !book.legenda.includes(c.nome)).map(c => <option key={c.nome} value={c.nome}>{c.nome}</option>)}
          </select>
        )}
      </div>

      <div className="ng-campos">
        <label className="ng-toggle"><input type="checkbox" checked={book.capaGeral} onChange={e => mudar({ capaGeral: e.target.checked })} /><span>Capa com título e período</span></label>
        {book.agruparPor && (
          <label className="ng-toggle"><input type="checkbox" checked={book.capaPorGrupo} onChange={e => mudar({ capaPorGrupo: e.target.checked })} /><span>Uma capa por grupo</span></label>
        )}
      </div>
      <label className="ng-campo"><span>Título da capa</span>
        <input maxLength={120} value={book.titulo ?? ''} placeholder="Em branco, o nome do relatório" onChange={e => mudar({ titulo: e.target.value || null })} />
      </label>

      <div className="ng-campo">
        <span>Filtros do book (independentes dos blocos do e-mail)</span>
        {book.filtros.length === 0 && <small>Sem filtro, vale tudo que a pessoa pode ver (até o limite). Costuma valer filtrar a data por um período.</small>}
        {book.filtros.map((f, i) => (
          <div key={i} className="dp-filtro">
            <select value={f.coluna} aria-label="Coluna do filtro" onChange={e => trocarFiltro(i, { coluna: e.target.value })}>
              <option value="">Escolha a coluna</option>
              {opcoes(f.coluna)}
            </select>
            <select className="ng-select" value={f.tipo} aria-label="Tipo do filtro"
                    onChange={e => trocarFiltro(i, e.target.value === 'relativo' ? { tipo: 'relativo', periodo: 'ultimos_7_dias', valores: null } : { tipo: 'valores', periodo: null, valores: [] })}>
              <option value="relativo">Período (anda com o calendário)</option>
              <option value="valores">Valores fixos</option>
            </select>
            {f.tipo === 'relativo' ? (
              <select className="ng-select" value={f.periodo ?? ''} aria-label="Período" onChange={e => trocarFiltro(i, { periodo: e.target.value })}>
                {periodos.map(p => <option key={p} value={p}>{PERIODOS[p] ?? p}</option>)}
              </select>
            ) : (
              <textarea className="dp-valores" rows={2} aria-label="Valores do filtro" placeholder="Um valor por linha"
                        value={(f.valores ?? []).join('\n')} onChange={e => trocarFiltro(i, { valores: lerValores(e.target.value) })} />
            )}
            <button type="button" className="ng-ico perigo" aria-label="Tirar o filtro" onClick={() => mudar({ filtros: book.filtros.filter((_, k) => k !== i) })}><CloseIcon fontSize="small" /></button>
          </div>
        ))}
        <div>
          <button type="button" className="ng-btn" onClick={() => mudar({ filtros: [...book.filtros, { coluna: '', tipo: 'relativo', periodo: 'ultimos_7_dias' }] })}>Adicionar filtro</button>
        </div>
      </div>
    </div>
  )
}
